import { createSessionsClient, readSessionListResponse, readSessionRecordResponse, readSessionConfirmationResponse, readSessionCancellationResponse, readSessionMessageResponse, readSessionApprovalResponse } from '@liquid-spirit/api/domains/sessions';
import apiClient from './apiClient';
import { invalidateActivityCaches } from './frontendCacheInvalidation';
import { captureNotificationRefresh } from './notificationRefresh';

const makeRequest = async (path, method = 'GET', body = null, config = {}) => {
  const {
    params,
    headers = {},
    signal,
    returnJson = method === 'GET',
  } = config;

  if (returnJson) {
    return apiClient.request(path, {
      method,
      body,
      params,
      headers,
      signal,
      contract: 'payload',
    });
  }

  await apiClient.request(path, {
    method,
    body,
    params,
    headers,
    signal,
    contract: 'payload',
  });
  return true;
};

// Session paths live in @liquid-spirit/api; transport and cache invalidation
// stay here.
const sessions = createSessionsClient({
  get: (path, options = {}) => makeRequest(path, 'GET', null, options),
  post: (path, body, options = {}) => makeRequest(path, 'POST', body, options),
  patch: (path, body, options = {}) => makeRequest(path, 'PATCH', body, options),
  delete: (path, options = {}) => makeRequest(path, 'DELETE', null, options),
});

const completeSessionAction = (request, validator = readSessionMessageResponse) =>
  request.then((payload) => {
    validator(payload);
    invalidateActivityCaches();
    return true;
  });

export const fetchSessions = (options = {}) =>
  sessions.list(options).then(readSessionListResponse);

export const fetchSessionById = (sessionId, options = {}) =>
  sessions.getById(sessionId, options).then(readSessionRecordResponse);

export const createSession = (activityId, sessionData, options = {}) => {
  const refreshNotifications = captureNotificationRefresh();
  return sessions.create(activityId, sessionData, { ...options, returnJson: true }).then((result) => {
    readSessionRecordResponse(result, 'session creation');
    invalidateActivityCaches({ activityId });
    refreshNotifications();
    return result;
  });
};

export const updateSession = (sessionId, updates, options = {}) =>
  sessions.update(sessionId, updates, { ...options, returnJson: true }).then((result) => {
    readSessionRecordResponse(result, 'session update');
    invalidateActivityCaches();
    return result;
  });

export const deleteSession = (sessionId, options = {}) =>
  completeSessionAction(sessions.remove(sessionId, { ...options, returnJson: true }));

export const cancelSession = (sessionId, options = {}) =>
  sessions.cancel(sessionId, { ...options, returnJson: true }).then((result) => {
    readSessionCancellationResponse(result);
    invalidateActivityCaches();
    return result;
  });

export const confirmSession = (sessionId, options = {}) =>
  sessions.confirm(sessionId, { ...options, returnJson: true }).then((result) => {
    readSessionConfirmationResponse(result);
    invalidateActivityCaches();
    return true;
  });

export const requestSessionParticipation = (sessionId, options = {}) =>
  completeSessionAction(sessions.requestParticipation(sessionId, { ...options, returnJson: true }));

export const cancelSessionParticipationRequest = (sessionId, userId, options = {}) =>
  completeSessionAction(sessions.cancelParticipationRequest(sessionId, userId, { ...options, returnJson: true }));

export const requestSessionFacilitator = (sessionId, options = {}) =>
  completeSessionAction(sessions.requestFacilitator(sessionId, { ...options, returnJson: true }));

export const cancelSessionFacilitatorRequest = (sessionId, userId, options = {}) =>
  completeSessionAction(sessions.cancelFacilitatorRequest(sessionId, userId, { ...options, returnJson: true }));

export const approveSessionParticipant = (sessionId, participantId, options = {}) =>
  completeSessionAction(sessions.approveParticipant(sessionId, participantId, { ...options, returnJson: true }), readSessionApprovalResponse);

export const denySessionParticipant = (sessionId, participantId, options = {}) =>
  completeSessionAction(sessions.denyParticipant(sessionId, participantId, { ...options, returnJson: true }));

export const approveSessionFacilitator = (sessionId, facilitatorId, options = {}) =>
  completeSessionAction(sessions.approveFacilitator(sessionId, facilitatorId, { ...options, returnJson: true }), readSessionApprovalResponse);

export const denySessionFacilitator = (sessionId, facilitatorId, options = {}) =>
  completeSessionAction(sessions.denyFacilitator(sessionId, facilitatorId, { ...options, returnJson: true }));

export const leaveSessionAsParticipant = (sessionId, userId, options = {}) =>
  completeSessionAction(sessions.leaveAsParticipant(sessionId, userId, { ...options, returnJson: true }));

export const leaveSessionAsFacilitator = (sessionId, userId, options = {}) =>
  completeSessionAction(sessions.leaveAsFacilitator(sessionId, userId, { ...options, returnJson: true }));

export default {
  fetchSessions,
  fetchSessionById,
  createSession,
  updateSession,
  deleteSession,
  cancelSession,
  confirmSession,
  requestSessionParticipation,
  cancelSessionParticipationRequest,
  requestSessionFacilitator,
  cancelSessionFacilitatorRequest,
  approveSessionParticipant,
  denySessionParticipant,
  approveSessionFacilitator,
  denySessionFacilitator,
  leaveSessionAsParticipant,
  leaveSessionAsFacilitator,
};
